/**
 * parseBodyDefaultStyle — draft/resend 재열람 시 편집기 기본 스타일 시딩용 파서 (draft-body-style-fidelity).
 *
 * 메일 본문 HTML 의 **최상위 wrapper** (`<body ... nine-mail-id>` 또는 legacy `<div nine-mail-id>`)
 * inline style 에서 편집기 기본값 후보(font-family / font-size / color)를 추출한다.
 *
 * 배경: 메일 본문 기본 글자색은 텍스트마다 박혀 있지 않고 최상위 `<body style="color:...">` 한 곳에
 * CSS 상속으로 존재한다. NgwEditor 는 로드 시 이 wrapper style 을 폐기하고 편집기 기본색을 사용자
 * 설정에서 재계산하므로, 답장색으로 저장한 draft 를 재열람하면 본문색이 사용자 설정(검정)으로 바뀐다.
 * 본 파서로 원본 wrapper style 을 읽어 `lifecycle.init` 의 defaultStyle 로 시딩(첫 마운트 前)한다.
 *
 * 규칙:
 *   1. 별도 `DOMParser('text/html')` 로 raw HTML 파싱 (unwrapEmailBodyHTML 의 `<div>`-wrap 재사용 금지).
 *   2. `<body ... nine-mail-id ...>` 가 있으면 그 style 이 authoritative (비어 있어도 fall-through 안 함).
 *   3. 없으면 최상위 non-region `div[nine-mail-id]` (isRegionDiv 제외) style.
 *   4. 둘 다 없으면(외부메일/마커 없음) 빈 객체 — 호출부가 사용자 설정 fallback.
 *
 * region div(quoted_header/quoted_body/nine_body 등)의 style 은 절대 읽지 않는다 (quoted 원문의
 * 자체 색 rgb(0,0,0) 오독 방지) — `isRegionDiv` 로 제외.
 *
 * line-height 는 파싱하지 않는다 — EditorDefaultStyle 에 필드가 없고, 편집기는
 * NgwEditorLifecycle.init 에서 NGW_DEFAULT_LINE_HEIGHT 로 별도 주입한다.
 *
 * @internal 단위 테스트에서 직접 검증.
 */

import type { EditorDefaultStyle } from '../types'
import { isRegionDiv } from './extensions/mailRegions'

export interface ParsedBodyStyle {
  fontFamily?: string
  fontSize?: string
  color?: string
}

function readWrapperStyle(el: HTMLElement): ParsedBodyStyle {
  const out: ParsedBodyStyle = {}
  const fontFamily = el.style.fontFamily.trim()
  const fontSize = el.style.fontSize.trim()
  const color = el.style.color.trim()
  if (fontFamily.length > 0) out.fontFamily = fontFamily
  if (fontSize.length > 0) out.fontSize = fontSize
  if (color.length > 0) out.color = color
  return out
}

/**
 * 메일 본문 HTML 의 최상위 wrapper inline style 에서 편집기 기본값 후보를 추출한다.
 * 추출 실패/미검출 시 빈 객체를 반환한다 (크래시 금지).
 */
export function parseBodyDefaultStyle(html: string): ParsedBodyStyle {
  if (typeof html !== 'string' || html.length === 0) return {}
  if (typeof DOMParser === 'undefined') return {}

  let doc: Document
  try {
    doc = new DOMParser().parseFromString(html, 'text/html')
  } catch {
    return {}
  }

  // 1) OMWA 신규 형태: `<body ... nine-mail-id ...>` — authoritative (빈 style 이어도 fall-through 안 함).
  const body = doc.body
  if (body != null && body.hasAttribute('nine-mail-id')) {
    return readWrapperStyle(body)
  }

  // 2) legacy: 최상위 non-region `div[nine-mail-id]`.
  const wrapper = Array.from(doc.querySelectorAll('div[nine-mail-id]')).find(
    (el) => !isRegionDiv(el),
  )
  if (wrapper != null) return readWrapperStyle(wrapper as HTMLElement)

  // 3) 외부메일 / 마커 없음 → 빈 객체 (호출부 사용자 설정 fallback).
  return {}
}

/**
 * draft 에서 파싱한 body style 을 사용자 설정(computeDefaultStyle 결과) 위에 속성별로 병합한다.
 * draft 에 있는 속성만 draft 값을 쓰고, 없는 속성은 사용자 설정값을 유지한다.
 */
export function mergeDraftDefaults(
  body: ParsedBodyStyle,
  user: EditorDefaultStyle,
): EditorDefaultStyle {
  return {
    fontFamily:
      body.fontFamily != null && body.fontFamily.length > 0 ? body.fontFamily : user.fontFamily,
    fontSize: body.fontSize != null && body.fontSize.length > 0 ? body.fontSize : user.fontSize,
    color: body.color != null && body.color.length > 0 ? body.color : user.color,
  }
}

/**
 * 편집 가능 region(nine_body / blank_sign / nine_signature)의 **color 미지정 텍스트**를
 * `<span style="color:{color}">` 로 래핑한다 (draft-body-style-fidelity).
 *
 * 배경: NgwEditor 툴바 글자색은 커서의 `textStyle` color mark 만 읽는다(라이브러리 소스 확인).
 * 상속 기반 body color (`<body style="color">`)나 컨테이너 color 는 mark 가 아니라 툴바에 반영되지
 * 않는다. 로드 HTML 의 본문 텍스트를 span 으로 감싸면 ProseMirror 가 파싱 시 textStyle color mark 로
 * 변환하므로, 커서 위치 색이 툴바에 반영되고 새 입력 텍스트도 인접 mark 를 이어받는다. span 은
 * setData 의 초기 파싱에 포함되어 별도 dispatch 없이 doc 기준선에 들어가므로 dirty 오판이 없다.
 *
 * 보존 규칙(래핑 제외):
 *   - 이미 color 를 가진 조상이 region 내부에 있는 텍스트 (예: 서명의 `<span style="color:rgb(125...)">`) — 자체 색 유지.
 *   - `<a>` 안의 텍스트 — 링크 색(라이브러리 `.ngw-editor a` 규칙) 보존.
 *   - quoted_header / quoted_body 영역 — 인용 원문 색 불변 (셀렉터가 x_ prefix 로 매칭 안 되나, closest 로 이중 방어).
 *   - 공백만 있는 텍스트.
 *
 * @internal 단위 테스트에서 직접 검증.
 */
const EDITABLE_REGION_SELECTOR =
  'div.nine_body, div[id^="nine_body_"], div.blank.sign, div[id^="blank_sign_"], div.nine_signature, div[id^="nine-sign-"]'
const QUOTED_REGION_SELECTOR =
  'div.quoted_body_editor, div[id^="quoted_body_"], div.quoted_header_editor, div[id^="quoted_header_"]'

export function wrapRegionTextWithDefaultColor(html: string, color: string): string {
  if (html.length === 0 || color.trim().length === 0) return html
  if (typeof DOMParser === 'undefined') return html

  const doc = new DOMParser().parseFromString(html, 'text/html')
  const regions = Array.from(doc.querySelectorAll(EDITABLE_REGION_SELECTOR)).filter(
    (region) => region.closest(QUOTED_REGION_SELECTOR) == null,
  )
  if (regions.length === 0) return html

  const hasColorAncestorWithin = (textNode: Text, region: Element): boolean => {
    let el: Element | null = textNode.parentElement
    while (el != null) {
      if (el.tagName === 'A') return true
      const c = (el as HTMLElement).style?.color
      if (c != null && c.trim().length > 0) return true
      if (el === region) break
      el = el.parentElement
    }
    return false
  }

  let modified = false
  for (const region of regions) {
    const walker = doc.createTreeWalker(region, NodeFilter.SHOW_TEXT)
    const texts: Text[] = []
    let cur = walker.nextNode()
    while (cur != null) {
      texts.push(cur as Text)
      cur = walker.nextNode()
    }
    for (const textNode of texts) {
      if (textNode.textContent == null || textNode.textContent.trim().length === 0) continue
      if (hasColorAncestorWithin(textNode, region)) continue
      const parent = textNode.parentNode
      if (parent == null) continue
      const span = doc.createElement('span')
      span.setAttribute('style', `color: ${color}`)
      parent.replaceChild(span, textNode)
      span.appendChild(textNode)
      modified = true
    }
  }
  if (!modified) return html

  return /<html[\s>]/i.test(html) ? doc.documentElement.outerHTML : doc.body.innerHTML
}
